import { vec2 } from 'gl-matrix';

/** Calculates the modulo of two numbers, always returning a positive value. */
export function mod(a: number, b: number) {
  return ((a % b) + b) % b;
}

export function clamp(min: number, max: number, value: number) {
  return Math.min(Math.max(min, value), max);
}

/**
 * Linearly interpolates between a and b.
 * Amount of 0 is a, amount of 1 is b
 * @param min
 * @param max
 * @param amount
 * @returns
 */
export function lerp(min: number, max: number, amount: number) {
  return min + (max - min) * amount;
}

/**
 * Linearly increases or decreases the given scale by the given amount.
 *
 * @param delta The amount to increase or decrease the scale by (e.g. 0.1 increases the
 *              scale by 10%, -0.2 decreases the scale by 20%)
 * @param scale The current scale
 * @returns The new scale
 */
export function linearScale(delta: number, scale: number) {
  return scale * (1 + delta);
}

/**
 * Given two points (x1, y1) and (x2, y2), determines the equation of the line passing through
 * both points, and uses that equation to return the y value for the x value given.
 * @param point1 Given point 1
 * @param point2 Given point 2
 * @param xToComputeFor The x value to compute the y value for
 * @returns The y value for the x value given
 */
export function evaluateLinearEquationFromTwoPoints(
  point1: { x: number; y: number },
  point2: { x: number; y: number },
  xToComputeFor: number,
) {
  const slope = (point2.y - point1.y) / (point2.x - point1.x);
  const yIntercept = point1.y - slope * point1.x;
  return slope * xToComputeFor + yIntercept;
}

export const toDegrees = (radians: number) => radians * (180 / Math.PI);

export const toRadians = (degrees: number) => degrees * (Math.PI / 180);

/**
 * Reduces an angle in radians to a value larger than -pi and smaller than or equal to pi
 * This matches Math.atan2() behaviour
 * @param radians
 */
export function reduceAngle(radians: number): number {
  return Math.PI - mod(Math.PI - radians, Math.PI * 2);
}

/**
 * Opposes an angle - points it in the opposite direction
 * @param radians
 * @returns
 */
export function opposeAngle(radians: number): number {
  return reduceAngle(radians + Math.PI);
}

/**
 * Takes an angle and rotates it to be perpendicular
 * @param angle
 * @param right - true = turn right, false = turn left
 * @returns
 */
export function perpendicularAngle(angle: number, right: boolean) {
  return angle + Math.PI * 0.5 * (right ? 1 : -1);
}

/**
 * Normalizes an angle to a range starting from lowerBound to lowerBound + Math.PI * 2
 * If a lowerBound is not provided, this normalizes to be between 0 and 2 * Math.PI
 * @param radians
 * @param lowerBound
 */
export function normalizeAngle(radians: number, lowerBound = 0): number {
  while (radians < lowerBound) {
    radians += 2 * Math.PI;
  }
  while (radians > lowerBound + 2 * Math.PI) {
    radians -= 2 * Math.PI;
  }

  return radians;
}

/**
 * Checks if two floating-point numbers are equal within a given tolerance.
 * @param floatA - The first floating-point number.
 * @param floatB - The second floating-point number.
 * @param tolerance - The tolerance for comparison (default: 0.001).
 * @returns True if the numbers are equal within the tolerance, false otherwise.
 */
export function isFloatEqual(floatA: number, floatB: number, tolerance = 0.001): boolean {
  const roundedDifferenceTo4Dp = Math.round(Math.abs(floatB - floatA) * 10000) / 10000;

  return roundedDifferenceTo4Dp <= tolerance;
}

/**
 * Calculates the angle between two vectors.
 * @param v1 - The first vector.
 * @param v2 - The second vector.
 * @returns The angle in radians between the two vectors.
 */
export function angleBetween(v1: vec2, v2: vec2): number {
  // Calculate the dot product
  const dot = vec2.dot(v1, v2);

  // Calculate the magnitude of the first vector
  const mag1 = vec2.length(v1);
  // Calculate the magnitude of the second vector
  const mag2 = vec2.length(v2);

  // Return the angle using the arccosine of the normalized dot product
  return Math.acos(safeDivide(dot, mag1 * mag2));
}

/**
 * Completes a safe float division between a given dividend and divisor.
 * @param dividend
 * @param divisor
 * @param fallback
 * @returns
 */
export const safeDivide = (
  dividend: number,
  divisor: number,
  fallback?: number,
): number => {
  // dividend = NaN falls here, but 0 is allowed
  if (!dividend && dividend !== 0) {
    // Returning 1 as a fallback. This is not used as a default value in the
    // fallback param because below, if the dividend is defined, we prefer to
    // return that over a fallback of 1 (in the default case)
    return 1;
  }

  // divisor = 0 or NaN falls here
  if (!divisor) {
    return fallback ?? dividend;
  }

  return dividend / divisor;
};

/**
 *  An alternative to avoid stack overflows caused by Math.max spreads
 */
export const max = (oneOrMoreNumbers: IterableIterator<number>) => {
  let largest = oneOrMoreNumbers.next().value;

  for (const number of oneOrMoreNumbers) {
    largest = largest > number ? largest : number;
  }

  return largest;
};

/**
 * An alternative to avoid stack overflows caused by Math.min spreads
 */
export const min = (oneOrMoreNumbers: IterableIterator<number>) => {
  let smallest = oneOrMoreNumbers.next().value;

  for (const number of oneOrMoreNumbers) {
    smallest = smallest < number ? smallest : number;
  }

  return smallest;
};
